import { Redirect, Route } from 'react-router-dom';
import { IonApp, IonRouterOutlet, setupIonicReact, IonButton, IonAlert } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import Home from './pages/Home';
import { useEffect, useState } from 'react';
import { getMessagingInstance } from './firebase';
import { getToken } from 'firebase/messaging';
import PushNotifications from './components/PushNotifications';
import UpdateNotification from './components/UpdateNotification';
import getApi from './api';
import { useLocalStorage } from './components/useLocalStorage';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import Login from './pages/Login';
import Customers from './pages/customers';
import Customer from './pages/customer';
import Weborders from './pages/weborders';
import Order from './pages/order';
import Offer from './pages/offer';
import Tid from './pages/tid';
import ProtectedRoute from './components/ProtectedRoute';
import Tidred from './pages/tidred';
import TimeRegistration from './pages/TimeRegistration';
import TimeRegistrationV2 from './pages/TimeRegistrationV2';
import TimeRegistrationV2Register from './pages/TimeRegistrationV2Register';
import PumpCustomers from './pages/pump_customers';
import PumpCustomer from './pages/pump_customer';
import OpenOrders from './pages/openorders/openorders';
import Pump from './pages/pump/Pump';
import Purchase from './pages/purchase/purchase';
import DeveloperSettings from './pages/DeveloperSettings';
import Settings from './pages/Settings';
import StocktakingList from './pages/stocktaking/StocktakingList';
import StocktakingDetail from './pages/stocktaking/StocktakingDetail';
import SickLeave from './pages/SickLeave';
import Vacation from './pages/Vacation';
import IncomingGoods from './pages/IncomingGoods';
import IncomingGoodsDetail from './pages/IncomingGoodsDetail';
import Notes from './pages/Notes';
import NoteDetail from './pages/NoteDetail';
import WarehouseTransfer from './pages/WarehouseTransfer';
import PLCParameters from './pages/pump/PLCParameters';
import VLTParameters from './pages/pump/VLTParameters';
import PumpsMenu from './pages/pump/PumpsMenu';


setupIonicReact({
  swipeBackEnabled: false,
});

const App: React.FC = () => {
  const [me, setMe] = useLocalStorage("me", "");
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  useEffect(() => {
    // Service Worker (inkl. Firebase Messaging) registreras i UpdateNotification-komponenten
    // Detta säkerställer att rätt SW används för både PWA-uppdateringar och push notifications

    // Initiera PushNotifications listener
    PushNotifications.initialize();

    // Kontrollera om vi redan har tillstånd eller om vi ska fråga
    const checkPermission = async () => {
      if (!('Notification' in window)) {
        console.log('Denna webbläsare stöder inte notifikationer');
        return;
      }

      const permission = Notification.permission;
      console.log('Nuvarande notifikationstillstånd:', permission);

      if (permission === 'granted') {
        // Om redan godkänt, hämta token direkt
        setupPushNotifications();
      } else if (permission === 'default') {
        // Kontrollera om användaren redan har tackat nej tidigare
        const hasDeclined = localStorage.getItem('notificationPromptDeclined');
        if (!hasDeclined) {
          // Visa prompt efter en längre fördröjning för att ge tid för navigation och inloggning
          setTimeout(() => {
            const isLoggedIn = localStorage.getItem('token');
            // Kontrollera att vi inte är på login-sidan
            if (isLoggedIn && window.location.pathname !== '/login') {
              setShowNotificationPrompt(true);
            }
          }, 3000);
        }
      }
    };

    checkPermission();
  }, []);

  const setupPushNotifications = async () => {
    try {
      const messagingInstance = await getMessagingInstance();
      if (messagingInstance) {
        const token = await getToken(messagingInstance, {
          vapidKey: 'BHImdpf-yGPWjQJ9uoJbXrI34I4X7WUi8QG8izL3CuyWXErSh-gv9sO4mUKK6jaFg5_vigOi96kjshskfNZ7Vyg',
        });
        console.log('FCM Token:', token);
        window.localStorage.setItem("FCMtoken", token);

        // Skicka token till backend om användaren är inloggad
        const authToken = localStorage.getItem("token");
        if (authToken && token) {
          try {
            await getApi().post('/user/fcm-token/', {
              registration_id: token,
              type: 'web'
            });
            console.log('FCM token skickad till backend');
          } catch (error) {
            console.error('Kunde inte skicka FCM token till backend:', error);
          }
        }
      } else {
        console.log('Firebase Cloud Messaging stöds inte i denna miljö');
      }
    } catch (error) {
      console.error('Fel vid hämtning av FCM token:', error);
    }
  };

  const handleEnableNotifications = async () => {
    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);

      if (permission === 'granted') {
        await setupPushNotifications();
      }
    } catch (error) {
      console.error('Fel vid begäran om notifikationstillstånd:', error);
    }
    setShowNotificationPrompt(false);
  };

  // Hämta /me vid app-start om användaren är inloggad
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const api = getApi();
      api.get("/user/me/")
        .then(res => {
          setMe(res.data);
        })
        .catch(err => {
          console.error("Failed to fetch user data:", err);
        });
    }
  }, []);

  return (
    <IonApp>
      <UpdateNotification />
      <IonReactRouter>
        <IonRouterOutlet placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Route exact path="/Home">
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Customers">
            <ProtectedRoute>
              <Customers />
            </ProtectedRoute>
          </Route>
          <Route exact path="/PumpsMenu">
            <ProtectedRoute>
              <PumpsMenu />
            </ProtectedRoute>
          </Route>
          <Route exact path="/PumpCustomers">
            <ProtectedRoute>
              <PumpCustomers />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Tid">
            <ProtectedRoute>
              <Tid />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Tid/:id">
            <ProtectedRoute>
              <Tidred />
            </ProtectedRoute>
          </Route>
          <Route exact path="/TimeRegistration">
            <ProtectedRoute>
              <TimeRegistration />
            </ProtectedRoute>
          </Route>
          <Route exact path="/TimeRegistrationV2">
            <ProtectedRoute>
              <TimeRegistrationV2 />
            </ProtectedRoute>
          </Route>
          <Route exact path="/TimeRegistrationV2/register/:projectId">
            <ProtectedRoute>
              <TimeRegistrationV2Register />
            </ProtectedRoute>
          </Route>
          <Route exact path="/TimeRegistrationV2/edit/:registrationId">
            <ProtectedRoute>
              <TimeRegistrationV2Register />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Weborders">
            <ProtectedRoute>
              <Weborders />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Customer/:id">
            <ProtectedRoute>
              <Customer />
            </ProtectedRoute>
          </Route>
          <Route exact path="/PumpCustomer/:id">
            <ProtectedRoute>
              <PumpCustomer />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Order/:id">
            <ProtectedRoute>
              <Order />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Offer/:id">
            <ProtectedRoute>
              <Offer />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Purchase">
            <ProtectedRoute>
              <Purchase />
            </ProtectedRoute>
          </Route>
          <Route exact path="/openorders/">
            <ProtectedRoute>
              <OpenOrders />
            </ProtectedRoute>
          </Route>
          <Route exact path="/stocktaking">
            <ProtectedRoute>
              <StocktakingList />
            </ProtectedRoute>
          </Route>
          <Route exact path="/stocktaking/:id">
            <ProtectedRoute>
              <StocktakingDetail />
            </ProtectedRoute>
          </Route>
          <Route exact path="/SickLeave">
            <ProtectedRoute>
              <SickLeave />
            </ProtectedRoute>
          </Route>
          <Route exact path="/Vacation">
            <ProtectedRoute>
              <Vacation />
            </ProtectedRoute>
          </Route>
          <Route exact path="/IncomingGoods">
            <ProtectedRoute>
              <IncomingGoods />
            </ProtectedRoute>
          </Route>
          <Route exact path="/IncomingGoods/:id">
            <ProtectedRoute>
              <IncomingGoodsDetail />
            </ProtectedRoute>
          </Route>
          <Route exact path="/WarehouseTransfer">
            <ProtectedRoute>
              <WarehouseTransfer />
            </ProtectedRoute>
          </Route>
          <Route exact path="/notes">
            <ProtectedRoute>
              <Notes />
            </ProtectedRoute>
          </Route>
          <Route exact path="/notes/:id">
            <ProtectedRoute>
              <NoteDetail />
            </ProtectedRoute>
          </Route>
          <Route exact path="/settings">
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          </Route>
          <Route exact path="/devsettings">
            <ProtectedRoute>
              <DeveloperSettings />
            </ProtectedRoute>
          </Route>
          <Route path="/pump/:userId/:id" exact={true}>
            <ProtectedRoute>
              <Pump />
            </ProtectedRoute>
          </Route>
          <Route path="/pump/:userId/:id/plc-parameters" exact={true}>
            <ProtectedRoute>
              <PLCParameters />
            </ProtectedRoute>
          </Route>
          <Route path="/pump/:userId/:id/vlt-parameters" exact={true}>
            <ProtectedRoute>
              <VLTParameters />
            </ProtectedRoute>
          </Route>

          <Route exact path="/login">
            <Login />
          </Route>
          <Route exact path="/">
            <Redirect to="/login" />
          </Route>
        </IonRouterOutlet>

        {/* iOS-vänlig notifikationsprompt */}
        <IonAlert
          isOpen={showNotificationPrompt}
          onDidDismiss={() => setShowNotificationPrompt(false)}
          header={'Aktivera notifikationer'}
          message={'Vill du få notifikationer när det händer något viktigt i appen?'}
          buttons={[
            {
              text: 'Inte nu',
              role: 'cancel',
              handler: () => {
                localStorage.setItem('notificationPromptDeclined', 'true');
                setShowNotificationPrompt(false);
              }
            },
            {
              text: 'Aktivera',
              handler: handleEnableNotifications
            }
          ]}
        />
      </IonReactRouter>
    </IonApp>
  );

}

export default App;
